import React from "react";
import { Box } from "@material-ui/core";
import {
  MultiSelect,
  CRadioGroup,
  CTextField,
  HFlex,
  ProgressButton,
  Text,
  ErrorBox,
  DialogSuccessNotice,
  useErrorContext,
} from "shared";
import { FormProvider, useForm } from "react-hook-form";
import { PhoneNumberField, withOptionalPhone } from "shared/PhoneNumberInput";
import { useTranslation } from "react-i18next";
import { Send } from "assets";
import useInvitePartner, { ClinicPartnerPayload } from "../../queries/useInvitePartner";
import { delay } from "utils/misc";
import { FORM_NOTIFICATION_DELAY } from "theme";
import { useMountState } from "utils/hooks";
import { css } from "@emotion/css";
import { usePortalPayerOptions } from "../utils/usePortalPayerOptions";
import { PayerKind } from "api/hooks/useHCPs";

interface Props {
  onClose: () => void;
}

export const InvitePartnerForm: React.VFC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const setError = useErrorContext();
  const getMountState = useMountState();
  const invitePartner = useInvitePartner();

  const form = useForm<ClinicPartnerPayload & { payer_kind: PayerKind }>({
    mode: "onChange",
    defaultValues: { payer_kind: "provider_group", payer_ids: [] },
  });
  const {
    formState: { errors },
  } = form;

  const payerKind = form.watch("payer_kind");
  const payerIds = form.watch("payer_ids");
  const providerGroups = usePortalPayerOptions("provider_group");
  const insurances = usePortalPayerOptions("insurance");
  const [shown, other] = payerKind === "insurance" ? [insurances, providerGroups] : [providerGroups, insurances];
  const { error, isError, isLoading } = shown;

  const payerOptions = [
    ...shown.options,
    ...other.options.filter(({ value }) => payerIds?.map(String).includes(value)),
  ];

  const submit = form.handleSubmit(async ({ payer_kind, ...fields }) => {
    // No resolver runs the schema, and `required` on a multi-select checks the empty text input, not the chips
    if (!fields.payer_ids.length) {
      form.setError("payer_ids", { type: "required" });
      return;
    }
    try {
      await invitePartner.mutateAsync({
        ...withOptionalPhone(fields),
        payer_ids: fields.payer_ids.map(Number),
        // TODO: Remove when not needed by API
        // @ts-ignore
        // sources: ["TEST"],
      });
      if (!getMountState()) return;
      await delay(FORM_NOTIFICATION_DELAY);
      if (!getMountState()) return;
      onClose();
    } catch (err) {
      setError(t`errors.could_not_send_invite`, err);
    }
  });

  if (invitePartner.isSuccess)
    return (
      <DialogSuccessNotice
        heading={t("patients.invite_sent")}
        body={`${t("form.invitation_successfully_sent_to")} ${invitePartner?.data?.email}`}
        onClose={onClose}
      />
    );

  return (
    <Box mx={3}>
      <Text heading bold mb={1} id="invite-partner">
        {t("form.invite_partner")}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <CTextField
            required
            name="email"
            autoFocus
            label={t("form.email")}
            type="email"
            margin="normal"
            size="small"
            fullWidth
          />
          <PhoneNumberField containerStyle={{ width: "100%", margin: "16px 0 8px" }} inputStyle={{ width: "100%" }} />
          <HFlex gap={2}>
            <CTextField
              fullWidth
              name="first_name"
              label={t("form.first_name")}
              margin="normal"
              size="small"
              required
            />
            <CTextField fullWidth name="last_name" label={t("form.last_name")} margin="normal" size="small" required />
          </HFlex>
          <div className={styles.spanRow}>
            <CRadioGroup
              name="payer_kind"
              row
              defaultValue="provider_group"
              aria-label={t("form.which_providers")}
              items={[
                { value: "provider_group", label: t("form.provider_groups") },
                { value: "insurance", label: t("form.insurance_companies") },
              ]}
            />
            <MultiSelect
              name="payer_ids"
              label={t("form.which_providers")}
              options={payerOptions}
              getOptionSelected={(option, value) => option.value === value.value}
              getOptionLabel={({ label }) => label || "NAME MISSING"}
              getOptionValue={({ value }) => value}
              loading={isLoading}
              // MultiSelect sets values without revalidating, so hide the error once a payer is picked
              error={!!errors.payer_ids && !payerIds?.length}
              margin="normal"
              multiple
              noOptionsText={isError && <ErrorBox error={error} />}
              size="small"
              disableCloseOnSelect
            />
          </div>
          <HFlex justifyContent="center" my={6}>
            <ProgressButton loading={invitePartner.isLoading} endIcon={<Send width={20} />}>
              {t("buttons.send_invitation")}
            </ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};

const styles = {
  label: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  spanRow: css`
    grid-column: 1 / span 2;
  `,
};
